'use client';

import Image from '@/components/Image';
import { useClientOnly } from '@/hooks/useClientOnly';
import { env } from '@/utils/env';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { StyleHotProductCategoryProduct, StyleHotProductRawAdCategory } from '../../types';
import {
  getStyleHotProductColorStateKey,
  registerStyleHotProductPageReturnScrollRestore,
} from '../../hooks/styleHotProductViewState';
import { useStyleCategoryProducts } from '../../hooks/useStyleCategoryProducts';
import { getInitialStyleCategoryTabIndex } from '../../utils';
import type { StyleCategoryCacheProps } from '../StyleCategory';
import StyleCategorySwiperWap from './StyleCategorySwiperWap';
import StyleProductListWap from './StyleProductListWap';

interface StyleProductVenueWapProps extends StyleCategoryCacheProps {
  data?: StyleHotProductRawAdCategory | null;
  marketingPageId?: number;
  adp?: string;
  isPreview?: boolean;
  inAppH5?: boolean;
  styleJumpRequest?: {
    styleName: string;
    requestId: number;
  } | null;
  onTrackNavTabClick?: (tabName: string, index: number, moduleName?: string) => void;
  onTrackProductClick?: (
    item: StyleHotProductCategoryProduct,
    index: number,
    moduleName: string,
    tabName?: string,
  ) => void;
  onTrackProductExposure?: (
    item: StyleHotProductCategoryProduct,
    index: number,
    moduleName: string,
    tabName?: string,
  ) => void;
}

const WAP_STICKY_BAR_HEIGHT = 54;
const WAP_STICKY_VISIBILITY_TOLERANCE = 1;
const STYLE_CATEGORY_SWIPER_MODULE_NAME = '商品分类版位-a';
const STYLE_CATEGORY_STICKY_MODULE_NAME = '商品分类版位-b';
const STYLE_CATEGORY_PRODUCT_MODULE_NAME = '商品分类版位';

function getWapStickyTopOffset({ inAppH5 }: { inAppH5?: boolean }): number {
  if (inAppH5) {
    return 0;
  }

  const headerEl = document.getElementById('header');

  if (!headerEl) {
    return 44;
  }

  const headerRect = headerEl.getBoundingClientRect();

  return Math.max(headerRect.bottom, 44);
}

function resolveAssetUrl(path?: string | null): string {
  if (!path) {
    return '';
  }

  if (/^(https?:)?\/\//.test(path) || path.startsWith('blob:')) {
    return path;
  }

  if (path.startsWith('/admin/')) {
    return `${env.NEXT_PUBLIC_S3_URL}${path}`;
  }

  return path;
}

function normalizeStyleName(styleName?: string | null): string {
  return (styleName || '').trim().toLowerCase();
}

function getStyleCategoryTabName(tab?: StyleHotProductRawAdCategory['tabs'][number]): string {
  return tab?.tab_title || tab?.tab_name || tab?.title || '';
}

type StyleStickyTabItem = {
  tab: NonNullable<StyleHotProductRawAdCategory['tabs']>[number];
  index: number;
};

function getOrderedStickyTabItems(
  tabs: NonNullable<StyleHotProductRawAdCategory['tabs']>,
  startTabIndex: number,
): StyleStickyTabItem[] {
  const startIndex = startTabIndex >= 0 && startTabIndex < tabs.length ? startTabIndex : 0;
  const tabItems = tabs.map((tab, index) => ({ tab, index }));

  return [...tabItems.slice(startIndex), ...tabItems.slice(0, startIndex)];
}

interface StyleStickyTabsWapProps {
  tabs: NonNullable<StyleHotProductRawAdCategory['tabs']>;
  activeTabIndex: number;
  isVisible: boolean;
  topOffset: number;
  orderStartIndex: number;
  initialScrollLeft: number;
  onTabClick: (index: number) => void;
  onScrollLeftChange: (scrollLeft: number) => void;
}

function StyleStickyTabsWap({
  tabs,
  activeTabIndex,
  isVisible,
  topOffset,
  orderStartIndex,
  initialScrollLeft,
  onTabClick,
  onScrollLeftChange,
}: StyleStickyTabsWapProps) {
  const isClient = useClientOnly();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const tabButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const scrollFrameRef = useRef(0);
  const revealTabButton = useCallback((index: number) => {
    window.requestAnimationFrame(() => {
      const container = scrollContainerRef.current;
      const button = tabButtonRefs.current[index];

      if (!container || !button) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const leftOverflow = buttonRect.left - containerRect.left;
      const rightOverflow = buttonRect.right - containerRect.right;
      const visibilityTolerance = 1;

      if (leftOverflow < -visibilityTolerance) {
        container.scrollBy({ left: leftOverflow, behavior: 'smooth' });
        return;
      }

      if (rightOverflow > visibilityTolerance) {
        container.scrollBy({ left: rightOverflow, behavior: 'smooth' });
      }
    });
  }, []);

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;

    if (container) {
      container.scrollLeft = initialScrollLeft;
    }
  }, [initialScrollLeft, isClient]);

  useEffect(
    () => () => {
      if (scrollFrameRef.current) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
    },
    [],
  );

  if (!isClient || !isVisible || tabs.length === 0) {
    return null;
  }

  const orderedTabs = getOrderedStickyTabItems(tabs, orderStartIndex);

  return createPortal(
    <div
      className="fixed left-0 z-[80] flex h-[54px] w-full items-center overflow-hidden bg-[linear-gradient(-45deg,rgba(255,255,255,0.16)_0%,rgba(255,255,255,0.05)_48%,rgba(0,0,0,0.2)_100%)] p-[12px] shadow-[inset_0_1px_0_rgba(255,255,255,0.8),inset_1px_0_0_rgba(255,80,100,0.08),inset_-1px_0_0_rgba(80,140,255,0.08),0_4px_16px_rgba(0,0,0,0.12)] [backdrop-filter:blur(22.5px)_saturate(150%)_contrast(105%)] [-webkit-backdrop-filter:blur(22.5px)_saturate(150%)_contrast(105%)] lg:hidden"
      style={{ top: topOffset }}
    >
      <div
        ref={scrollContainerRef}
        className="flex w-full items-center gap-[8px] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        onScroll={(event) => {
          const scrollLeft = event.currentTarget.scrollLeft;

          if (scrollFrameRef.current) {
            window.cancelAnimationFrame(scrollFrameRef.current);
          }

          scrollFrameRef.current = window.requestAnimationFrame(() => {
            onScrollLeftChange(scrollLeft);
          });
        }}
      >
        {orderedTabs.map(({ tab, index }) => {
          const title = tab.tab_title || tab.tab_name || tab.title || '';
          const imageSrc = resolveAssetUrl(
            tab.wap_sticky_image || tab.pc_sticky_image || tab.wap_tab_image || tab.pc_tab_image,
          );
          const isActive = index === activeTabIndex;

          return (
            <button
              type="button"
              ref={(element) => {
                tabButtonRefs.current[index] = element;
              }}
              key={tab.uid || tab.id || `${title}-${index}`}
              className={`flex h-[30px] w-[93px] shrink-0 items-center gap-[4px] overflow-hidden rounded-[675px] border-0 p-[3px] transition-colors ${
                isActive ? 'bg-[#463ae8] text-white' : 'bg-white text-[#282828]'
              }`}
              onClick={() => {
                onTabClick(index);
                revealTabButton(index);
              }}
            >
              {imageSrc && (
                <span className="relative h-[24px] w-[24px] shrink-0 overflow-hidden rounded-full">
                  <Image
                    src={imageSrc}
                    alt={title}
                    fill
                    sizes="24px"
                    className="block h-full w-full object-cover object-center"
                  />
                </span>
              )}
              {title && (
                <span className="min-w-0 truncate text-left font-br text-[12px] font-normal leading-normal">
                  {title}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}

export default function StyleProductVenueWap({
  data,
  marketingPageId,
  adp,
  isPreview,
  inAppH5,
  activeTabIndex,
  viewState,
  restoredFromCache,
  updateViewState,
  styleJumpRequest,
  onTrackNavTabClick,
  onTrackProductClick,
  onTrackProductExposure,
}: StyleProductVenueWapProps) {
  const title = data?.section_title || '';
  const subtitle = data?.section_subtitle || '';
  const defaultActiveTabIndex = getInitialStyleCategoryTabIndex(data);
  const { tabs, isActiveTabLoading } = useStyleCategoryProducts({
    data,
    activeTabIndex,
    marketingPageId,
    adp,
    isPreview,
  });
  const isStickyTabsVisible = viewState.stickyTabsVisible;
  const [stickyTopOffset, setStickyTopOffset] = useState(44);
  const [pendingScrollRequestId, setPendingScrollRequestId] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const categorySwiperRef = useRef<HTMLDivElement>(null);
  const firstProductRowRef = useRef<HTMLDivElement>(null);
  const stickyTabScrollRequestIdRef = useRef(0);
  const autoScrolledAdpRef = useRef<string | null>(null);
  const cacheRestorePendingRef = useRef(restoredFromCache);
  const productNavigationPendingRef = useRef(false);
  const activeTab = useMemo(
    () => tabs[activeTabIndex] || tabs[defaultActiveTabIndex] || tabs[0],
    [activeTabIndex, defaultActiveTabIndex, tabs],
  );
  const setStickyTabsVisible = useCallback(
    (visible: boolean) => {
      if (viewState.stickyTabsVisible !== visible) {
        updateViewState({
          stickyTabsVisible: visible,
          ...(visible ? { stickyTabsOrderStartIndex: activeTabIndex } : {}),
        });
      }
    },
    [activeTabIndex, updateViewState, viewState.stickyTabsVisible],
  );
  const getPendingScrollTargetElement = useCallback(
    () => (isActiveTabLoading ? null : firstProductRowRef.current),
    [isActiveTabLoading],
  );
  const restoreCachedPageScroll = useCallback(() => {
    if (viewState.pageScrollY === null || viewState.pageScrollY === undefined) {
      return false;
    }

    window.scrollTo({ top: viewState.pageScrollY, left: 0, behavior: 'auto' });
    return true;
  }, [viewState.pageScrollY]);

  useLayoutEffect(() => {
    restoreCachedPageScroll();
  }, [restoreCachedPageScroll]);

  const syncStickyTabsVisibility = useCallback(() => {
    const sectionEl = sectionRef.current;
    const firstProductRowEl = firstProductRowRef.current;

    if (!sectionEl || !firstProductRowEl || window.innerWidth >= 1024) {
      setStickyTabsVisible(false);
      return;
    }

    const sectionRect = sectionEl.getBoundingClientRect();
    const firstProductRowRect = firstProductRowEl.getBoundingClientRect();
    const nextStickyTopOffset = getWapStickyTopOffset({ inAppH5 });
    const shouldShow =
      firstProductRowRect.top <= nextStickyTopOffset + WAP_STICKY_VISIBILITY_TOLERANCE &&
      sectionRect.bottom > nextStickyTopOffset + WAP_STICKY_BAR_HEIGHT;

    setStickyTopOffset((prev) => (prev === nextStickyTopOffset ? prev : nextStickyTopOffset));
    setStickyTabsVisible(shouldShow);
  }, [inAppH5, setStickyTabsVisible]);

  useEffect(() => {
    let animationFrameId = 0;
    let restoreFrameId = 0;

    const scheduleCachedScrollRestore = () => {
      if (!restoreCachedPageScroll()) {
        return false;
      }

      cacheRestorePendingRef.current = true;

      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (restoreFrameId) {
        window.cancelAnimationFrame(restoreFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        restoreCachedPageScroll();
        restoreFrameId = window.requestAnimationFrame(() => {
          restoreCachedPageScroll();
          animationFrameId = 0;
          restoreFrameId = 0;
          cacheRestorePendingRef.current = false;
        });
      });

      return true;
    };

    const requestSync = () => {
      if (
        animationFrameId ||
        cacheRestorePendingRef.current ||
        productNavigationPendingRef.current
      ) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = 0;
        syncStickyTabsVisibility();
      });
    };

    if (cacheRestorePendingRef.current) {
      if (!scheduleCachedScrollRestore()) {
        cacheRestorePendingRef.current = false;
        syncStickyTabsVisibility();
      }
    } else {
      syncStickyTabsVisibility();
    }
    window.addEventListener('scroll', requestSync, { passive: true });
    window.addEventListener('resize', requestSync);
    window.addEventListener('pageshow', scheduleCachedScrollRestore);
    window.addEventListener('popstate', scheduleCachedScrollRestore);

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (restoreFrameId) {
        window.cancelAnimationFrame(restoreFrameId);
      }

      window.removeEventListener('scroll', requestSync);
      window.removeEventListener('resize', requestSync);
      window.removeEventListener('pageshow', scheduleCachedScrollRestore);
      window.removeEventListener('popstate', scheduleCachedScrollRestore);
    };
  }, [restoreCachedPageScroll, syncStickyTabsVisibility]);

  useEffect(() => {
    if (!cacheRestorePendingRef.current) {
      syncStickyTabsVisibility();
    }
  }, [activeTabIndex, syncStickyTabsVisibility]);

  useEffect(() => {
    if (!styleJumpRequest?.styleName) {
      return;
    }

    const targetStyleName = normalizeStyleName(styleJumpRequest.styleName);
    const targetTabIndex = tabs.findIndex((tab) => {
      const tabStyleName = normalizeStyleName(tab.tab_title || tab.tab_name || tab.title);

      return tabStyleName === targetStyleName;
    });

    if (targetTabIndex < 0) {
      return;
    }

    updateViewState({ activeTabIndex: targetTabIndex });
    setPendingScrollRequestId(styleJumpRequest.requestId);
    setStickyTopOffset(getWapStickyTopOffset({ inAppH5 }));
    setStickyTabsVisible(true);
  }, [inAppH5, setStickyTabsVisible, styleJumpRequest, tabs, updateViewState]);

  useEffect(() => {
    if (pendingScrollRequestId === null) {
      return;
    }

    let firstAnimationFrameId = 0;
    let secondAnimationFrameId = 0;

    firstAnimationFrameId = window.requestAnimationFrame(() => {
      secondAnimationFrameId = window.requestAnimationFrame(() => {
        const scrollTargetEl = getPendingScrollTargetElement();

        if (!scrollTargetEl) {
          if (isActiveTabLoading) {
            return;
          }

          setPendingScrollRequestId(null);
          return;
        }

        const nextStickyTopOffset = getWapStickyTopOffset({ inAppH5 });
        const targetTop =
          scrollTargetEl.getBoundingClientRect().top + window.scrollY - nextStickyTopOffset;

        window.scrollTo({
          top: Math.max(targetTop, 0),
          behavior: 'smooth',
        });

        if (window.innerWidth < 1024) {
          setStickyTopOffset(nextStickyTopOffset);
          setStickyTabsVisible(true);
        }

        setPendingScrollRequestId(null);
      });
    });

    return () => {
      if (firstAnimationFrameId) {
        window.cancelAnimationFrame(firstAnimationFrameId);
      }

      if (secondAnimationFrameId) {
        window.cancelAnimationFrame(secondAnimationFrameId);
      }
    };
  }, [
    activeTabIndex,
    getPendingScrollTargetElement,
    inAppH5,
    isActiveTabLoading,
    pendingScrollRequestId,
    setStickyTabsVisible,
  ]);

  const handleTabChange = useCallback(
    (index: number) => {
      if (index === activeTabIndex) {
        return;
      }

      updateViewState({ activeTabIndex: index });
      onTrackNavTabClick?.(
        getStyleCategoryTabName(tabs[index]),
        index,
        STYLE_CATEGORY_SWIPER_MODULE_NAME,
      );
    },
    [activeTabIndex, onTrackNavTabClick, tabs, updateViewState],
  );

  const handleStickyTabClick = useCallback(
    (index: number) => {
      onTrackNavTabClick?.(
        getStyleCategoryTabName(tabs[index]),
        index,
        STYLE_CATEGORY_STICKY_MODULE_NAME,
      );

      stickyTabScrollRequestIdRef.current += 1;
      setStickyTopOffset(getWapStickyTopOffset({ inAppH5 }));
      updateViewState({ activeTabIndex: index, stickyTabsVisible: true });
      setPendingScrollRequestId(stickyTabScrollRequestIdRef.current);
      setStickyTabsVisible(true);
    },
    [inAppH5, onTrackNavTabClick, setStickyTabsVisible, tabs, updateViewState],
  );

  const handleCategorySwiperUserNavigate = useCallback(
    (swiperEl: HTMLElement) => {
      if (window.innerWidth >= 1024) {
        return;
      }

      const nextStickyTopOffset = getWapStickyTopOffset({ inAppH5 });
      const swiperRect = swiperEl.getBoundingClientRect();
      const targetTop = swiperRect.top + window.scrollY - nextStickyTopOffset;

      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: 'smooth',
      });
    },
    [inAppH5],
  );

  useEffect(() => {
    if (
      !adp ||
      tabs.length === 0 ||
      pendingScrollRequestId !== null ||
      autoScrolledAdpRef.current === adp ||
      restoredFromCache
    ) {
      return;
    }

    const swiperEl = categorySwiperRef.current;

    if (!swiperEl) {
      return;
    }

    let secondAnimationFrameId = 0;
    const firstAnimationFrameId = window.requestAnimationFrame(() => {
      secondAnimationFrameId = window.requestAnimationFrame(() => {
        autoScrolledAdpRef.current = adp;
        handleCategorySwiperUserNavigate(swiperEl);
      });
    });

    return () => {
      window.cancelAnimationFrame(firstAnimationFrameId);
      if (secondAnimationFrameId) {
        window.cancelAnimationFrame(secondAnimationFrameId);
      }
    };
  }, [
    adp,
    handleCategorySwiperUserNavigate,
    pendingScrollRequestId,
    restoredFromCache,
    tabs.length,
  ]);

  const handleProductClick = useCallback(
    async (item: StyleHotProductCategoryProduct, index: number) => {
      const pageScrollY = window.scrollY;
      const pageUrl = `${window.location.pathname}${window.location.search}`;

      productNavigationPendingRef.current = true;
      await updateViewState({
        pageScrollY,
        stickyTabsVisible: isStickyTabsVisible,
      });
      registerStyleHotProductPageReturnScrollRestore({
        pageUrl,
        scrollY: pageScrollY,
        onRestored: () => {
          productNavigationPendingRef.current = false;
        },
      });
      onTrackProductClick?.(
        item,
        index,
        STYLE_CATEGORY_PRODUCT_MODULE_NAME,
        getStyleCategoryTabName(activeTab),
      );
    },
    [activeTab, isStickyTabsVisible, onTrackProductClick, updateViewState],
  );
  const handleProductColorChange = useCallback(
    (item: StyleHotProductCategoryProduct, colorIndex: number) => {
      const productKey = getStyleHotProductColorStateKey(item);

      if (!productKey || viewState.selectedColorByProductKey[productKey] === colorIndex) {
        return;
      }

      updateViewState({
        selectedColorByProductKey: {
          ...viewState.selectedColorByProductKey,
          [productKey]: colorIndex,
        },
      });
    },
    [updateViewState, viewState.selectedColorByProductKey],
  );
  const handleStickyTabsScrollLeftChange = useCallback(
    (scrollLeft: number) => {
      if (Math.abs(viewState.stickyTabsScrollLeft - scrollLeft) > 1) {
        updateViewState({ stickyTabsScrollLeft: scrollLeft });
      }
    },
    [updateViewState, viewState.stickyTabsScrollLeft],
  );

  return (
    <section ref={sectionRef} className="w-full overflow-hidden bg-black pt-[40px] text-white">
      <div className="mx-auto flex w-full flex-col items-center">
        {(title || subtitle) && (
          <div className="flex w-[352px] flex-col items-end gap-[8px] text-right text-white">
            {title && (
              <h2 className="w-full truncate text-right font-bs text-[24px] uppercase leading-[normal] text-white">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="line-clamp-4 w-full font-br text-[12px] leading-[15px] text-white">
                {subtitle}
              </p>
            )}
          </div>
        )}

        <div className="relative left-1/2 mt-[32px] w-screen -translate-x-1/2 bg-[linear-gradient(180deg,#000_0%,#404040_100%)]">
          <div ref={categorySwiperRef} className="w-full">
            <StyleCategorySwiperWap
              tabs={tabs}
              activeTabIndex={activeTabIndex}
              onActiveTabChange={handleTabChange}
              onUserNavigate={handleCategorySwiperUserNavigate}
            />
          </div>
          <div className="h-[32px] w-full" />
        </div>
        <div className="relative left-1/2 w-screen -translate-x-1/2 bg-[linear-gradient(180deg,#404040_0%,#000_100%)] pb-[40px]">
          <StyleProductListWap
            products={activeTab?.products}
            tabName={getStyleCategoryTabName(activeTab)}
            firstRowRef={firstProductRowRef}
            isLoading={isActiveTabLoading}
            selectedColorByProductKey={viewState.selectedColorByProductKey}
            onProductClick={handleProductClick}
            onProductColorChange={handleProductColorChange}
            onProductExposure={(item, index) =>
              onTrackProductExposure?.(
                item,
                index,
                STYLE_CATEGORY_PRODUCT_MODULE_NAME,
                getStyleCategoryTabName(activeTab),
              )
            }
          />
        </div>
      </div>
      {isStickyTabsVisible && (
        <StyleStickyTabsWap
          tabs={tabs}
          activeTabIndex={activeTabIndex}
          isVisible={isStickyTabsVisible}
          topOffset={stickyTopOffset}
          orderStartIndex={viewState.stickyTabsOrderStartIndex}
          initialScrollLeft={viewState.stickyTabsScrollLeft}
          onTabClick={handleStickyTabClick}
          onScrollLeftChange={handleStickyTabsScrollLeftChange}
        />
      )}
    </section>
  );
}
